import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Modal from 'Components/Modal';
import { useMixpanel } from 'Context/AnalyticsContext';
import { Box, Text } from 'Components/Base';
import {
  ReviewContent,
  ReviewFooter as BaseReviewFooter,
  ReviewQuestionnaire,
  ReviewRatingFeedback,
} from 'Components/Review';
import { useGetFeedbackTemplate, useGiveFeedback } from 'Util/FeedBackService';
import { useDisclosure } from 'Util/Hooks';
import { useMyCourseData } from 'Domain/MyCourseData';
import { myProfile } from 'Redux/user-profile/selectors';
import { sendDataToGTM, GTM_EVENT_NAME, GTM_DATA_LAYER_PROPERTY } from 'Util/Analytics';

import { RECOMMEND_RATING, EXPECTATION_RATING } from './ReviewOptions';

export const NEXT_REVIEW_AT = {
  PROGRESS_80: 'PROGRESS_80',
  PROGRESS_100: 'PROGRESS_100',
  NONE: 'NONE',
};

const ReviewFooter = ({ ...props }) => (
  <BaseReviewFooter
    flexDirection={{ _: 'column-reverse', md: 'row' }}
    justifyContent={'center'}
    alignItems={'center'}
    {...props}
  />
);

const STEPS = {
  CONGRATS: 0,
  RECOMMENDED: 1,
  EXPECTATION: 2,
  REVIEW: 3,
  LIKE_TOPIC: 4,
  THANKS: 5,
};

const LAST_STEP = STEPS.THANKS;
const FIRST_STEP = STEPS.CONGRATS;

const Review = ({
  isShow,
  onCloseReview,
  progressPercent,
  review: reviewData,
  revision,
  contentType = 'COURSE',
  user,
  meta,
  ...props
}) => {
  const mixpanel = useMixpanel();

  const [step, setStep] = useState(0);
  const [isOpen, , onClose] = useDisclosure({ isOpen: isShow, onClose: onCloseReview });

  const { data: myCourseData, courseCode } = useMyCourseData();

  const { data: feedbackTemplateData, isSuccess } = useGetFeedbackTemplate({
    templateId: 'SKOOLDIO_FEEDBACK',
  });

  const { giveFeedback: giveReview } = useGiveFeedback({
    templateId: 'SKOOLDIO_FEEDBACK',
  });

  const recommendedInitialState = reviewData?.recommendation
    ? [`${reviewData?.recommendation}`]
    : [];
  const expectationInitialState = reviewData?.expectation ? [`${reviewData?.expectation}`] : [];

  const [recommended, setRecommended] = useState(recommendedInitialState);
  const [expectation, setExpectation] = useState(expectationInitialState);
  const [topics, setTopics] = useState(reviewData?.topics ?? []);
  const [rating, setRating] = useState(reviewData?.rating ?? null);
  const [review, setReview] = useState(reviewData?.feedback ?? '');

  const { t } = useTranslation('classroom');

  const handleRecommend = (value) => setRecommended(value);
  const handleExpectation = (value) => setExpectation(value);
  const handleTopics = (value) => setTopics(value);
  const handleRating = (event, value) => setRating(value);
  const handleFeedback = (event) => setReview(event.target.value);

  const courseTitle = myCourseData.courseInfo?.title;

  const { options: likeTopics } = useMemo(() => {
    const { options } =
      feedbackTemplateData?.getTemplate?.components?.find(
        (component) => component.id === 'TOPICS'
      ) ?? [];

    return {
      options: options?.map((option) => ({
        label: option.title,
        value: option.value,
      })),
    };
  }, [feedbackTemplateData]);

  const handlePreviousStep = () => {
    if (step !== 0) {
      setStep(step - 1);
    }
  };

  const handleNextStep = () => {
    if (step === FIRST_STEP) {
      if (mixpanel) {
        mixpanel.track('Review Course', {
          'Course Name': courseTitle,
          Step: 'Start',
        });
      }

      // logging GTM event
      sendDataToGTM({
        event: GTM_EVENT_NAME.REVIEW_START_BUTTON_CLICK,
        [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: courseTitle,
      });
    }

    if (step !== LAST_STEP) {
      setStep(step + 1);
    }
  };

  const handleCloseReviewModal = () => {
    if (mixpanel) {
      mixpanel.track('Review Course', {
        'Course Name': courseTitle,
        Step: 'Finish',
      });
    }

    // logging GTM event
    if (step === FIRST_STEP) {
      sendDataToGTM({
        event: GTM_EVENT_NAME.REVIEW_LATER_BUTTON_CLICK,
        [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: courseTitle,
      });
    }

    onClose();
  };

  const renderHeader = useMemo(() => {
    if (step === STEPS.CONGRATS || step === STEPS.THANKS) {
      return <Box pt={7} />;
    }
    return (
      <Text textStyle={'caption'} fontWeight={'normal'} textAlign={'center'} mb={7}>
        {`${step} / ${LAST_STEP - 1}`}
      </Text>
    );
  }, [step]);

  // TODO: Move title and description text to locale file
  const renderContent = () => {
    switch (step) {
      case STEPS.CONGRATS: {
        if (progressPercent === 100) {
          return (
            <ReviewContent
              key={step}
              title={t('COURSE_REVIEW.REVIEW_MODAL.GRADUATE_CONTENT.TITLE')}
              description={t('COURSE_REVIEW.REVIEW_MODAL.GRADUATE_CONTENT.PARAGRAPH')}
            />
          );
        }
        return (
          <ReviewContent
            key={step}
            title={'คุณมาไกลเกินครึ่งแล้ว!'}
            description={
              'คิดว่าคอร์สนี้เป็นอย่างไรบ้าง? สามารถตอบแบบสอบถามสั้น ๆ เพื่อช่วยปรับปรุงคุณภาพของคอร์สให้ดียิ่งขึ้น'
            }
          />
        );
      }
      case STEPS.RECOMMENDED: {
        return (
          <ReviewQuestionnaire
            key={step}
            question={'จากประสบการณ์การเรียนคอร์สนี้คุณจะแนะนำคอร์สนี้ให้เพื่อนมากน้อยแค่ไหน'}
            value={recommended ?? []}
            answerList={RECOMMEND_RATING}
            onChange={handleRecommend}
          />
        );
      }
      case STEPS.EXPECTATION: {
        return (
          <ReviewQuestionnaire
            key={step}
            question={'คอร์สนี้ตรงกับความคาดหวังหรือไม่'}
            value={expectation ?? []}
            answerList={EXPECTATION_RATING}
            onChange={handleExpectation}
          />
        );
      }
      case STEPS.REVIEW: {
        return (
          <ReviewRatingFeedback
            key={step}
            ratingValue={rating ?? null}
            onChangeRating={handleRating}
            feedbackValue={review ?? ''}
            onChangeFeedback={handleFeedback}
          />
        );
      }
      case STEPS.LIKE_TOPIC: {
        return (
          <ReviewQuestionnaire
            key={step}
            question={'สิ่งที่ชอบในคอร์สนี้'}
            description={'เลือกได้มากกว่า 1 ข้อ'}
            value={topics ?? []}
            answerableCount={likeTopics.length}
            answerList={likeTopics}
            onChange={handleTopics}
          />
        );
      }
      case STEPS.THANKS: {
        return (
          <ReviewContent
            key={step}
            title={t('COURSE_REVIEW.REVIEW_MODAL.THANKS_CONTENT.TITLE')}
            description={t('COURSE_REVIEW.REVIEW_MODAL.THANKS_CONTENT.PARAGRAPH')}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  // TODO: Move label text to locale file
  const renderFooter = () => {
    switch (step) {
      case STEPS.CONGRATS: {
        return (
          <ReviewFooter
            primaryButtonLabel={'เริ่มทำแบบสอบถาม'}
            onPrimaryButtonClick={handleNextStep}
            secondaryButtonLabel={'ไว้ทีหลัง'}
            onSecondaryButtonClick={handleCloseReviewModal}
          />
        );
      }
      case STEPS.RECOMMENDED: {
        return (
          <ReviewFooter
            primaryButtonLabel={'ถัดไป'}
            onPrimaryButtonClick={handleNextStep}
            isPrimaryDisabled={recommended.length === 0}
            secondaryButtonLabel={'ย้อนกลับ'}
            onSecondaryButtonClick={handlePreviousStep}
          />
        );
      }
      case STEPS.EXPECTATION: {
        return (
          <ReviewFooter
            primaryButtonLabel={'ถัดไป'}
            onPrimaryButtonClick={handleNextStep}
            isPrimaryDisabled={expectation.length === 0}
            secondaryButtonLabel={'ย้อนกลับ'}
            onSecondaryButtonClick={handlePreviousStep}
          />
        );
      }
      case STEPS.REVIEW: {
        return (
          <ReviewFooter
            primaryButtonLabel={'ถัดไป'}
            onPrimaryButtonClick={handleNextStep}
            isPrimaryDisabled={rating === null}
            secondaryButtonLabel={'ย้อนกลับ'}
            onSecondaryButtonClick={handlePreviousStep}
          />
        );
      }
      case STEPS.LIKE_TOPIC: {
        return (
          <ReviewFooter
            primaryButtonLabel={'ส่งความคิดเห็น'}
            onPrimaryButtonClick={() => {
              giveReview(
                courseCode,
                user.id,
                {
                  firstname: user.firstname,
                  lastname: user.lastname,
                  updateRevision: revision,
                  ...meta,
                },
                contentType,
                {
                  recommendation: +recommended[0],
                  feedback: review,
                  expectation: +expectation[0],
                  rating,
                  topics,
                },
                { shouldUpdate: true }
              );
              handleNextStep();
            }}
            secondaryButtonLabel={'ย้อนกลับ'}
            onSecondaryButtonClick={handlePreviousStep}
          />
        );
      }
      case STEPS.THANKS: {
        return (
          <ReviewFooter primaryButtonLabel={'ปิด'} onPrimaryButtonClick={handleCloseReviewModal} />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <Modal
      header={renderHeader}
      contents={renderContent()}
      footer={renderFooter()}
      footerProps={{ mt: 7 }}
      muiProps={{ isActive: isOpen && isSuccess }}
      {...props}
    />
  );
};

Review.propTypes = {
  isShow: PropTypes.bool,
  progressPercent: PropTypes.number,
  user: PropTypes.object,
  meta: PropTypes.any,
  contentType: PropTypes.string,
};

export default connect((state) => {
  const user = myProfile(state);
  return { user };
}, {})(Review);
